import React, { useState, useEffect } from 'react';
import { 
  CreditCard, Banknote, Building2, FileText,
  Loader2, Check, PoundSterling, RefreshCw, X, Zap, AlertCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const MANUAL_PAYMENT_METHODS = [
  { id: 'cash', label: 'Cash', icon: Banknote, color: 'bg-green-100 text-green-700 border-green-200 hover:bg-green-200' },
  { id: 'bank_transfer', label: 'Bank Transfer', icon: Building2, color: 'bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-200' },
  { id: 'card', label: 'Card (Manual)', icon: CreditCard, color: 'bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-200' },
  { id: 'bacs', label: 'BACS', icon: Building2, color: 'bg-indigo-100 text-indigo-700 border-indigo-200 hover:bg-indigo-200' },
  { id: 'standing_order', label: 'Standing Order', icon: RefreshCw, color: 'bg-orange-100 text-orange-700 border-orange-200 hover:bg-orange-200' },
  { id: 'cheque', label: 'Cheque', icon: FileText, color: 'bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-200' },
  { id: 'other', label: 'Other', icon: PoundSterling, color: 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200' },
];

const TakePaymentModal = ({ 
  isOpen, 
  onClose, 
  customer,
  job,
  invoice,
  onSuccess
}) => {
  const [step, setStep] = useState(1); // 1 = select method, 2 = confirm charge, 3 = manual details
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [chargeType, setChargeType] = useState(null); // 'stripe' or 'gocardless'
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [reference, setReference] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showManualMethods, setShowManualMethods] = useState(false);
  const [sendReceipt, setSendReceipt] = useState(true);

  // Check if customer has saved payment methods
  const hasStripeCard = customer?.stripe_customer_id && customer?.stripe_has_payment_method;
  const hasGoCardless = customer?.gocardless_customer_id && (customer?.gocardless_has_mandate || customer?.payment_method === 'gocardless');

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSelectedMethod(null);
      setChargeType(null);
      setNotes('');
      setReference('');
      setShowManualMethods(false);
      setIsSubmitting(false);
      setSendReceipt(true);
      
      if (job?.price) {
        setAmount(job.price.toString());
      } else if (invoice?.total) {
        setAmount(invoice.total.toString());
      } else if (customer?.balance && customer.balance > 0) {
        setAmount(customer.balance.toString());
      } else {
        setAmount('');
      }
    }
  }, [isOpen, customer, job, invoice]);

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
      setChargeType(null);
    } else if (step === 3) {
      setShowManualMethods(true);
      setSelectedMethod(null);
      setStep(1);
    } else if (showManualMethods) {
      setShowManualMethods(false);
    }
  };

  // Show confirmation for Stripe
  const handleSelectStripe = () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setChargeType('stripe');
    setStep(2);
  };

  // Show confirmation for GoCardless
  const handleSelectGoCardless = () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setChargeType('gocardless');
    setStep(2);
  };

  // Confirm and charge Stripe card
  const handleConfirmStripeCharge = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    try {
      setIsSubmitting(true);
      const token = localStorage.getItem('streakfree_token');
      
      console.log('Charging Stripe card:', { customer_id: customer.id, amount: parseFloat(amount) });
      
      const response = await fetch(`${API}/api/stripe/charge`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customer_id: customer.id,
          amount: parseFloat(amount),
          description: `Payment from ${customer.name}`,
          invoice_id: invoice?.id || null
        })
      });

      const data = await response.json();
      console.log('Stripe charge response:', data);
      
      if (response.ok) {
        toast.success(`Card charged £${parseFloat(amount).toFixed(2)} successfully!`, {
          duration: 5000
        });
        // Small delay to ensure toast is visible before modal closes
        setTimeout(() => {
          onSuccess?.(data);
          onClose();
        }, 500);
      } else {
        toast.error(data.detail || 'Failed to charge card');
      }
    } catch (error) {
      console.error('Error charging card:', error);
      toast.error('Failed to charge card: ' + (error.message || 'Network error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Confirm and charge GoCardless
  const handleConfirmGoCardlessCharge = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    try {
      setIsSubmitting(true);
      const token = localStorage.getItem('streakfree_token');
      
      console.log('Charging GoCardless:', { customer_id: customer.id, amount: parseFloat(amount) });
      
      const response = await fetch(`${API}/api/gocardless/charge`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customer_id: customer.id,
          amount: parseFloat(amount),
          description: `Payment from ${customer.name}`,
          invoice_id: invoice?.id || null
        })
      });

      const data = await response.json();
      console.log('GoCardless charge response:', data);
      
      if (response.ok) {
        toast.success(`Direct Debit of £${parseFloat(amount).toFixed(2)} initiated! Funds will be collected in 3-5 days.`, {
          duration: 5000
        });
        // Small delay to ensure toast is visible before modal closes
        setTimeout(() => {
          onSuccess?.(data);
          onClose();
        }, 500);
      } else {
        toast.error(data.detail || 'Failed to charge Direct Debit');
      }
    } catch (error) {
      console.error('Error charging GoCardless:', error);
      toast.error('Failed to charge Direct Debit: ' + (error.message || 'Network error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Record manual payment
  const handleManualPayment = async () => {
    if (!selectedMethod || !amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      setIsSubmitting(true);
      const token = localStorage.getItem('streakfree_token');

      const payload = {
        customer_id: customer.id,
        amount: parseFloat(amount),
        payment_method: selectedMethod.id,
        notes: notes || null,
        reference: reference || null,
        job_id: job?.id || null,
        invoice_id: invoice?.id || null,
        send_receipt: sendReceipt && !!customer?.email
      };

      const response = await fetch(`${API}/api/payments/record`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Payment of £${parseFloat(amount).toFixed(2)} recorded`);
        onSuccess?.(data);
        onClose();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to record payment');
      }
    } catch (error) {
      console.error('Error recording payment:', error);
      toast.error('Failed to record payment');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md sm:max-w-lg" data-testid="take-payment-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {(step === 2 || step === 3 || showManualMethods) && (
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={handleBack} 
                className="h-8 w-8"
              >
                <X size={16} />
              </Button>
            )}
            <span>
              {step === 2 ? 'Confirm Payment' : 'Take Payment'}
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* Step 2: Confirmation Screen */}
        {step === 2 && chargeType && (
          <div className="space-y-4">
            {/* Charge Amount Box */}
            <div className={`p-6 rounded-xl text-center ${
              chargeType === 'stripe' 
                ? 'bg-gradient-to-br from-blue-500 to-blue-600' 
                : 'bg-gradient-to-br from-teal-500 to-teal-600'
            } text-white`}>
              <p className="text-sm opacity-90 mb-1">
                {chargeType === 'stripe' ? 'Charge to Card' : 'Charge via Direct Debit'}
              </p>
              <p className="text-4xl font-bold">£{parseFloat(amount).toFixed(2)}</p>
              <div className="mt-3 pt-3 border-t border-white/20">
                <p className="text-sm opacity-90">{customer?.name}</p>
                {chargeType === 'stripe' && customer?.stripe_card_last4 && (
                  <p className="text-xs opacity-75 mt-1">
                    {customer.stripe_card_brand} •••• {customer.stripe_card_last4}
                  </p>
                )}
                {chargeType === 'gocardless' && (
                  <p className="text-xs opacity-75 mt-1">GoCardless Direct Debit</p>
                )}
              </div>
            </div>

            {/* Warning */}
            <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={18} />
              <p className="text-sm text-amber-800">
                {chargeType === 'stripe' 
                  ? 'This will immediately charge the customer\'s saved card.'
                  : 'This will initiate a Direct Debit collection. Funds typically arrive in 3-5 working days.'
                }
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleBack}
                className="flex-1"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={chargeType === 'stripe' ? handleConfirmStripeCharge : handleConfirmGoCardlessCharge}
                disabled={isSubmitting}
                className={`flex-1 ${
                  chargeType === 'stripe'
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-teal-600 hover:bg-teal-700'
                }`}
                data-testid="confirm-charge-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={16} />
                    Processing...
                  </>
                ) : (
                  <>
                    <Check className="mr-2" size={16} />
                    Confirm Charge
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step 1: Select Payment Method */}
        {step === 1 && !showManualMethods && (
          <>
            {/* Customer Info Banner */}
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <p className="font-medium text-gray-900">{customer?.name}</p>
              {customer?.balance > 0 && (
                <p className="text-sm text-gray-500">
                  Outstanding balance: <span className="text-red-600 font-medium">£{customer.balance?.toFixed(2)}</span>
                </p>
              )}
            </div>

            {/* Amount Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount to charge</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                <Input
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="pl-7 text-lg font-semibold"
                  placeholder="0.00"
                  data-testid="payment-amount-input"
                />
              </div>
            </div>

            <div className="space-y-3">
              {/* Automatic Payment Options */}
              {(hasStripeCard || hasGoCardless) && (
                <>
                  <p className="text-sm font-medium text-gray-700">Charge saved payment method:</p>
                  <div className="space-y-2">
                    {hasStripeCard && (
                      <button
                        onClick={handleSelectStripe}
                        disabled={!amount || parseFloat(amount) <= 0}
                        className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all disabled:opacity-50"
                        data-testid="select-stripe-btn"
                      >
                        <div className="flex items-center gap-3">
                          <CreditCard size={24} />
                          <div className="text-left">
                            <p className="font-semibold">Pay by Saved Card</p>
                            <p className="text-sm text-blue-100">
                              {customer.stripe_card_brand} •••• {customer.stripe_card_last4}
                            </p>
                          </div>
                        </div>
                        <Zap size={20} />
                      </button>
                    )}
                    
                    {hasGoCardless && (
                      <button
                        onClick={handleSelectGoCardless}
                        disabled={!amount || parseFloat(amount) <= 0}
                        className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-xl hover:from-teal-600 hover:to-teal-700 transition-all disabled:opacity-50"
                        data-testid="select-gocardless-btn"
                      >
                        <div className="flex items-center gap-3">
                          <Building2 size={24} />
                          <div className="text-left">
                            <p className="font-semibold">Pay by Direct Debit</p>
                            <p className="text-sm text-teal-100">GoCardless</p>
                          </div>
                        </div>
                        <Zap size={20} />
                      </button>
                    )}
                  </div>
                  
                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-gray-500">or record manual payment</span>
                    </div>
                  </div>
                </>
              )}

              {/* Manual Payment Button */}
              <button
                onClick={() => setShowManualMethods(true)}
                className="w-full flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-600 hover:border-gray-400 hover:bg-gray-50 transition-all"
                data-testid="show-manual-methods-btn"
              >
                <Banknote size={20} />
                <span className="font-medium">Record Manual Payment</span>
              </button>
            </div>
          </>
        )}

        {/* Manual Payment Methods Grid */}
        {step === 1 && showManualMethods && !selectedMethod && (
          <>
            {/* Amount Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                <Input
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="pl-7 text-lg font-semibold"
                  placeholder="0.00"
                />
              </div>
            </div>
            
            <div className="space-y-3">
              <p className="text-sm text-gray-500">Select payment method:</p>
              <div className="grid grid-cols-2 gap-3">
                {MANUAL_PAYMENT_METHODS.map(method => {
                  const Icon = method.icon;
                  return (
                    <button
                      key={method.id}
                      onClick={() => { setSelectedMethod(method); setStep(3); }}
                      className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${method.color}`}
                      data-testid={`payment-method-${method.id}`}
                    >
                      <Icon size={24} />
                      <span className="font-medium text-sm">{method.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* Step 3: Manual Payment Details */}
        {step === 3 && selectedMethod && (
          <div className="space-y-4">
            <div className={`flex items-center gap-3 p-3 rounded-lg ${selectedMethod.color}`}>
              <selectedMethod.icon size={20} />
              <span className="font-medium">{selectedMethod.label}</span>
            </div>

            {/* Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                <Input
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="pl-7"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reference (optional)</label>
              <Input
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="e.g., Transaction ID"
                data-testid="payment-reference-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes..."
                rows={2}
                data-testid="payment-notes-input"
              />
            </div>

            {customer?.email && (
              <label className="flex items-center gap-2.5 p-3 bg-emerald-50 rounded-lg cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendReceipt}
                  onChange={(e) => setSendReceipt(e.target.checked)}
                  className="w-4 h-4 accent-emerald-600"
                  data-testid="send-receipt-checkbox"
                />
                <span className="text-sm text-emerald-900">
                  Email receipt to {customer.email}
                </span>
              </label>
            )}

            <Button
              onClick={handleManualPayment}
              disabled={isSubmitting || !amount}
              className="w-full"
              data-testid="record-payment-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Recording...
                </>
              ) : (
                <>
                  <Check className="mr-2" size={16} />
                  Record Payment of £{parseFloat(amount || 0).toFixed(2)}
                </>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TakePaymentModal;
